import {
  getRelayEntry,
  getRelayResultPresentation,
  getRelayStore,
  subscribeRelayRegistry,
} from '@github-ui/ahp-relay/relay-registry'
import {
  isResultPresentationEnabled,
  observeResultPresentation,
  type RelayResultPresentationSource,
  reportResultPresentationError,
  type ResultPresentationBinding,
  resultPresentationSourceWatermark,
} from '@github-ui/ahp-relay/relay-result-presentation'
import type {ResultPresentationCoverageReason} from '@github-ui/ahp-relay/result-presentation-health'
import type {EstablishedAhpSessionIdentity} from '@github-ui/ahp-session-client/contracts'
import {browserObservationTime, nextBrowserObservationSequence} from './browser-observation-clock'
import {createResultPresentationMetricObserver} from './browser-outcome-metrics'
import {turnMetrics} from './turn-metrics'
import {createBrowserResultPresentationTracker, samePresentationBinding} from './result-presentation'
import {
  RESULT_PRESENTATION_EVENT,
  type ResultPresentationFact,
  type ResultPresentationMilestone,
} from './result-presentation-contract'
import {sendEvent} from '@github-ui/hydro-analytics'

import {createResultPresentationObserver} from './result-presentation-observer'

export interface CopilotResultRenderIdentity extends Partial<ResultPresentationBinding> {
  readonly turn_id: string
  readonly part_id?: string
  readonly terminal_state?: 'complete' | 'cancelled' | 'error'
}

export interface CopilotResultControls {
  readonly input: HTMLTextAreaElement | null
  readonly submit: HTMLButtonElement | null
  readonly stop: HTMLButtonElement | null
  readonly readOnly: boolean
  readonly validInputActionEnabled: boolean
  readonly hasSubmittableContent: boolean
  readonly actionMode: 'send' | 'steer' | 'queue'
}

export interface CopilotResultPresentationView {
  coverageLost(reason: ResultPresentationCoverageReason, opportunity?: string): void
  renderFailed(): void
  register(element: HTMLElement, identity: CopilotResultRenderIdentity, kind: 'content' | 'terminal'): () => void
  controls(controls: CopilotResultControls): () => void
  dispose(reason?: 'disposed' | 'replaced' | 'page_exit'): void
}

const MAX_PRESENTATION_TARGETS = 256
const MAX_PRESENTATION_LISTENERS = 16
const MAX_PRESENTATION_VIEWS = 64
// Animation frames to keep content observed after its terminal milestone closes, so a late paint witness delivered
// after the pre-paint animation-frame callback can still resolve it before retirement.
const POST_PAINT_RECONCILIATION_FRAMES = 2
const activeViews = new Set<() => void>()

interface RenderedResult {
  element: HTMLElement
  identity: CopilotResultRenderIdentity
  kind: 'content' | 'terminal'
  unwatch?: () => void
}

export function beginCopilotResultPresentation(
  sessionIdentity: EstablishedAhpSessionIdentity,
  surface: ResultPresentationFact['surface'],
  eligibleSince?: number,
  sourceWatermark = resultPresentationSourceWatermark(),
  cacheBeforeExposure = true,
): CopilotResultPresentationView | undefined {
  if (!isResultPresentationEnabled() || !sessionIdentity.taskId) return undefined
  const taskId = sessionIdentity.taskId
  return observeResultPresentation(() => {
    const snapshot = () => {
      const entry = getRelayEntry(taskId)
      if (
        !entry ||
        entry.sessionId !== sessionIdentity.sessionId ||
        entry.environmentId !== sessionIdentity.environmentId
      )
        return
      return {
        state: getRelayStore(taskId)?.getSnapshot(),
        sessionState: entry.sessionState,
        passive: entry.passive,
        passiveKnown: entry.passiveKnown,
        live: entry.hasConnected,
      }
    }
    const clock = browserObservationTime()
    const rendered = new Set<RenderedResult>()
    const contentReconciliations = new Map<ResultPresentationMilestone, number>()
    const releaseResult = (result: RenderedResult) => {
      if (!rendered.delete(result)) return
      result.unwatch?.()
      result.unwatch = undefined
    }
    const releaseMilestoneTargets = (milestone: ResultPresentationMilestone) => {
      if (!milestone.closed) return
      for (const result of rendered) {
        if (
          result.identity.chat_id === milestone.receipt.chat_id &&
          result.identity.turn_id === milestone.receipt.turn_id &&
          result.kind === milestone.receipt.milestone_kind
        ) {
          releaseResult(result)
        }
      }
    }
    const reconcileContentPaint = (content: ResultPresentationMilestone) => {
      // A terminal milestone can close from the observer's pre-paint animation-frame callback. If content rendered in
      // the same frame, its Element Timing entry may not exist yet and `flushPaint()` only drains queued entries, so
      // keep content observed through a bounded post-paint window before retiring it and releasing its targets.
      observer.flushPaint()
      if (content.closed) {
        releaseMilestoneTargets(content)
        return
      }
      if (!observer.paintSupported || typeof requestAnimationFrame !== 'function') {
        content.retire()
        releaseMilestoneTargets(content)
        return
      }
      if (contentReconciliations.has(content)) return
      const attempt = (remaining: number) => {
        observer.flushPaint()
        if (!content.closed && !disposed && remaining > 0) {
          contentReconciliations.set(
            content,
            requestAnimationFrame(() => observeResultPresentation(() => attempt(remaining - 1))),
          )
          return
        }
        contentReconciliations.delete(content)
        if (!content.closed) content.retire()
        releaseMilestoneTargets(content)
      }
      contentReconciliations.set(
        content,
        requestAnimationFrame(() => observeResultPresentation(() => attempt(POST_PAINT_RECONCILIATION_FRAMES - 1))),
      )
    }
    let currentControls: CopilotResultControls | undefined
    let disposed = false
    let visibleSince = document.visibilityState === 'visible' ? clock.monotonicMs : undefined
    let resumedVisibility = false
    const currentRole = (): ResultPresentationFact['role'] => {
      const current = snapshot()
      if (!current?.passiveKnown) return 'unknown'
      if (current.passive) return 'passive'
      if (currentControls?.readOnly || current.state?.interactivity === 'read-only') return 'read_only'
      return current.state ? 'writable' : 'unknown'
    }
    const metrics = createResultPresentationMetricObserver(
      undefined,
      () => reportResultPresentationError('emission'),
      turnMetrics,
    )
    const tracker = createBrowserResultPresentationTracker(
      {
        now: browserObservationTime,
        nextSequence: nextBrowserObservationSequence,
        visibility: () => (document.visibilityState === 'visible' ? 'visible' : 'hidden'),
        role: currentRole,
        emit: fact => {
          metrics.observe(fact)
          sendEvent(
            RESULT_PRESENTATION_EVENT,
            {...fact},
            {
              privacy: 'content_free',
              onError: () => reportResultPresentationError('emission'),
            },
          )
        },
        reportError: reportResultPresentationError,
      },
      {
        client_view_id: crypto.randomUUID(),
        surface,
        binding: {
          task_id: taskId,
          session_id: sessionIdentity.sessionId,
          environment_id: sessionIdentity.environmentId,
        },
      },
    )
    const fail = () => {
      const time = browserObservationTime()
      for (const milestone of tracker.all()) milestone.failed('render_failed', time)
    }
    if (activeViews.size >= MAX_PRESENTATION_VIEWS) {
      tracker.coverageLost('observation_capacity')
      return {
        coverageLost: (reason, opportunity) => tracker.coverageLost(reason, undefined, opportunity),
        renderFailed: () => tracker.coverageLost('observation_capacity', undefined, 'render_failed'),
        register: (_element, identity, kind) => {
          tracker.coverageLost('observation_capacity', undefined, `${identity.turn_id}/${kind}`)
          return () => undefined
        },
        controls: () => {
          tracker.coverageLost('observation_capacity', undefined, 'controls')
          return () => undefined
        },
        dispose: reason => {
          tracker.dispose(reason)
          metrics.dispose()
        },
      }
    }
    activeViews.add(fail)
    const bound = (result: RenderedResult, milestone: ResultPresentationMilestone): boolean => {
      const current = snapshot()
      const expectedChat = sessionIdentity.channel?.startsWith('ahp-chat:')
        ? sessionIdentity.channel
        : current?.sessionState?.defaultChat
      const {task_id, session_id, environment_id, chat_id} = result.identity
      if (!expectedChat || !task_id || !session_id || !environment_id || !chat_id || !current?.state) {
        milestone.unresolved('binding_unknown')
        return false
      }
      if (
        expectedChat !== chat_id ||
        current.state.resource !== chat_id ||
        !current.sessionState?.chats.some(chat => chat.resource === chat_id) ||
        !samePresentationBinding(milestone.receipt, {task_id, session_id, environment_id, chat_id})
      ) {
        milestone.failed('binding_mismatch')
        return false
      }
      if (current.state.interactivity === 'hidden') {
        milestone.unresolved('hidden')
        return false
      }
      return true
    }
    const observeControls = (milestone: ResultPresentationMilestone) => {
      const actual = currentControls
      if (!actual?.input) return milestone.unresolved('controls_unobserved')
      const inputVisibility = observer.visibility(actual.input)
      if (inputVisibility !== true) {
        if (inputVisibility) milestone.unresolved(inputVisibility)
        return
      }
      const current = snapshot()
      const role = currentRole()
      if (role === 'unknown') return milestone.unresolved('role_unknown')
      const active = current?.state?.activeTurn?.id
      const blocked = role === 'passive' || role === 'read_only'
      const submitVisible = actual.submit && observer.visibility(actual.submit)
      const stopVisible = actual.stop && observer.visibility(actual.stop)
      const actualInputEnabled = !actual.input.readOnly && !actual.input.disabled
      const populatedActionEnabled =
        actual.validInputActionEnabled &&
        submitVisible === true &&
        (!actual.hasSubmittableContent ||
          (!actual.submit?.disabled && actual.submit.getAttribute('aria-disabled') !== 'true'))
      const submitName =
        actual.submit?.getAttribute('aria-label') ??
        actual.submit
          ?.getAttribute('aria-labelledby')
          ?.split(/\s+/)
          .map(id => document.getElementById(id)?.textContent ?? '')
          .join(' ')
      const validActionName = actual.actionMode !== 'send' || submitName?.trim() === 'Send message'
      milestone.controls({
        ...milestone.receipt,
        role,
        control_scenario: blocked ? role : active ? 'newer_active' : 'settled_writable',
        ...(active ? {active_turn_id: active} : {}),
        action_mode: blocked ? 'blocked' : actual.actionMode,
        input_editable: actualInputEnabled,
        send_enabled_for_valid_input: blocked
          ? Boolean(actual.submit && !actual.submit.disabled && actual.submit.getAttribute('aria-disabled') !== 'true')
          : populatedActionEnabled && validActionName && (!active || (stopVisible === true && !actual.stop?.disabled)),
        stop_present: stopVisible === true,
      })
    }
    const observeResult = (result: RenderedResult, paintTime?: number) => {
      const {identity, kind, element} = result
      if (!identity.chat_id) return
      if (tracker.exhausted) {
        tracker.coverageLost('observation_capacity', undefined, `${identity.turn_id}/${kind}`)
        return
      }
      const milestone = tracker.get(identity.chat_id, identity.turn_id, kind)
      if (!milestone || milestone.closed || !bound(result, milestone)) return
      if (
        paintTime !== undefined &&
        (visibleSince === undefined || paintTime < visibleSince || (resumedVisibility && paintTime === visibleSince))
      ) {
        milestone.unresolved('hidden')
        return
      }
      if (kind === 'content' && milestone.receipt.part_id !== identity.part_id) return
      const visible = observer.visibility(element, paintTime !== undefined)
      if (visible !== true) {
        if (visible) milestone.unresolved(visible)
        return
      }
      if (!element.textContent?.trim()) {
        milestone.unresolved(kind === 'content' ? 'content_unobserved' : 'terminal_unobserved')
        return
      }
      if (kind === 'terminal') {
        const state = snapshot()?.state
        const terminal = state?.turns.find(turn => turn.id === identity.turn_id)
        if (!terminal || terminal.state !== identity.terminal_state) {
          milestone.unresolved('terminal_unobserved')
          return
        }
      }
      const now = browserObservationTime()
      milestone.presented({
        ...milestone.receipt,
        ...(identity.terminal_state ? {terminal_state: identity.terminal_state} : {}),
        witness_method: paintTime === undefined ? 'dom_visible_frame' : 'element_timing',
        witness_time_ms: paintTime ?? now.monotonicMs,
        clock_id: clock.clockId,
      })
      if (kind === 'terminal') {
        observeControls(milestone)
        if (milestone.closed) {
          const content = tracker.get(identity.chat_id, identity.turn_id, 'content')
          if (content) reconcileContentPaint(content)
        }
      } else if (!observer.paintSupported) milestone.unresolved('paint_unsupported')
      releaseMilestoneTargets(milestone)
    }
    const observer = createResultPresentationObserver(
      () => {
        for (const result of rendered) observeResult(result)
      },
      (root, renderTime) => {
        for (const result of rendered) {
          if (result.element === root && result.kind === 'content') observeResult(result, renderTime)
        }
      },
    )
    let source: RelayResultPresentationSource | undefined
    let adoptedSource: RelayResultPresentationSource | undefined
    let unsubscribeSource: (() => void) | undefined
    const attach = () => {
      if (disposed) return
      const next = getRelayResultPresentation(taskId, sessionIdentity)
      observer.schedule()
      if (source !== next) {
        unsubscribeSource?.()
        source = next
        unsubscribeSource = next?.subscribe(
          receipt => {
            tracker.begin(receipt)
            observer.schedule()
          },
          eligibleSince ?? clock.monotonicMs,
          sourceWatermark,
          loss => tracker.coverageLost('observation_capacity', loss),
        )
      }
      const current = snapshot()
      if (next && !next.exhausted && adoptedSource !== next && current?.live && current.state) {
        adoptedSource = next
        const cached = next.adopt(current.state, eligibleSince ?? clock.monotonicMs, sourceWatermark)
        if (cached.length && !cacheBeforeExposure) tracker.coverageLost('entry_unverified')
        else for (const receipt of cached) tracker.begin(receipt)
      }
    }
    const unsubscribeRegistry = subscribeRelayRegistry(attach)
    attach()
    const visibilityChanged = () => {
      if (document.visibilityState !== 'visible') {
        visibleSince = undefined
        for (const milestone of tracker.all()) milestone.unresolved('hidden')
      } else {
        visibleSince = browserObservationTime().monotonicMs
        resumedVisibility = true
        if (tracker.exhausted) tracker.coverageLost('observation_capacity', undefined, 'visibility_return')
        observer.schedule()
      }
    }
    const pageExit = (event: PageTransitionEvent) => {
      if (event.persisted) {
        visibleSince = undefined
        for (const milestone of tracker.all()) milestone.unresolved('hidden')
        return
      }
      view.dispose('page_exit')
    }
    const pageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || disposed) return
      visibleSince = document.visibilityState === 'visible' ? browserObservationTime().monotonicMs : undefined
      resumedVisibility = true
      attach()
      observer.schedule()
    }
    document.addEventListener('visibilitychange', visibilityChanged)
    window.addEventListener('pagehide', pageExit)
    window.addEventListener('pageshow', pageShow)
    const view: CopilotResultPresentationView = {
      coverageLost: (reason, opportunity) => tracker.coverageLost(reason, undefined, opportunity),
      renderFailed: () => observeResultPresentation(fail),
      register(element, identity, kind) {
        if (disposed) return () => undefined
        if (tracker.exhausted) {
          tracker.coverageLost('observation_capacity', undefined, `${identity.turn_id}/${kind}`)
          return () => undefined
        }
        const milestone = identity.chat_id ? tracker.get(identity.chat_id, identity.turn_id, kind) : undefined
        if (!milestone || milestone.closed || (kind === 'content' && milestone.receipt.part_id !== identity.part_id))
          return () => undefined
        if (tracker.exhausted || rendered.size >= MAX_PRESENTATION_TARGETS) {
          tracker.coverageLost('observation_capacity', undefined, `${identity.turn_id}/${kind}`)
          return () => undefined
        }
        const result: RenderedResult = {element, identity, kind}
        rendered.add(result)
        result.unwatch = observer.watch(element, kind === 'content')
        return () => releaseResult(result)
      },
      controls(controls) {
        if (tracker.exhausted) {
          tracker.coverageLost('observation_capacity', undefined, 'controls')
          return () => undefined
        }
        currentControls = controls
        const cleanups = [controls.input, controls.submit, controls.stop].flatMap(element => {
          if (!element) return []
          return [observer.watch(element)]
        })
        observer.schedule()
        return () => {
          if (currentControls === controls) currentControls = undefined
          for (const cleanup of cleanups) cleanup()
        }
      },
      dispose(reason = 'disposed') {
        if (disposed) return
        disposed = true
        for (const handle of contentReconciliations.values()) cancelAnimationFrame(handle)
        contentReconciliations.clear()
        unsubscribeSource?.()
        unsubscribeRegistry()
        observer.flushPaint()
        tracker.dispose(reason)
        metrics.dispose()
        observer.dispose()
        activeViews.delete(fail)
        document.removeEventListener('visibilitychange', visibilityChanged)
        window.removeEventListener('pagehide', pageExit)
        window.removeEventListener('pageshow', pageShow)
        rendered.clear()
      },
    }
    return view
  })
}

export function createResultPresentationSelection(surface: ResultPresentationFact['surface']) {
  let view: CopilotResultPresentationView | undefined
  let key: string | undefined
  let mounted = false
  let eligibleSince: number | undefined
  let watermark: number | undefined
  let borrowed = false
  let listenerCapacity = false
  const listeners = new Set<() => void>()
  const identityKey = (identity: EstablishedAhpSessionIdentity | undefined) =>
    identity
      ? JSON.stringify([identity.taskId, identity.environmentId, identity.sessionId, identity.channel])
      : undefined
  const update = (
    identity: EstablishedAhpSessionIdentity | undefined,
    beforeExposure = true,
    retirement: 'disposed' | 'replaced' = 'disposed',
  ) =>
    observeResultPresentation(() => {
      const nextKey = identityKey(identity)
      if (key === nextKey) return
      if (key !== undefined) {
        eligibleSince = browserObservationTime().monotonicMs
        watermark = resultPresentationSourceWatermark()
      }
      if (!borrowed) view?.dispose(retirement)
      borrowed = false
      key = nextKey
      view = identity
        ? beginCopilotResultPresentation(identity, surface, eligibleSince, watermark, beforeExposure)
        : undefined
      if (listenerCapacity) view?.coverageLost('observation_capacity')
      for (const listener of listeners) observeResultPresentation(listener)
    })
  return {
    select: (identity: EstablishedAhpSessionIdentity | undefined, beforeExposure = true) =>
      update(identity, beforeExposure),
    retire: (reason: 'disposed' | 'replaced' = 'disposed') => update(undefined, true, reason),
    adopt(identity: EstablishedAhpSessionIdentity, selected: CopilotResultPresentationView | undefined) {
      if (view === selected) return
      const nextKey = identityKey(identity)
      if (!borrowed) view?.dispose(key === nextKey ? 'replaced' : 'disposed')
      borrowed = true
      key = nextKey
      view = selected
      for (const listener of listeners) observeResultPresentation(listener)
    },
    getSnapshot: () => view,
    subscribe(listener: () => void) {
      if (listeners.size >= MAX_PRESENTATION_LISTENERS) {
        listenerCapacity = true
        view?.coverageLost('observation_capacity', 'subscription')
        return () => undefined
      }
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    mount() {
      if (!isResultPresentationEnabled()) return () => undefined
      mounted = true
      eligibleSince ??= observeResultPresentation(() => browserObservationTime().monotonicMs)
      watermark ??= resultPresentationSourceWatermark()
      return () => {
        mounted = false
        queueMicrotask(() => {
          if (!mounted) {
            update(undefined)
            eligibleSince = undefined
            watermark = undefined
          }
        })
      }
    },
  }
}

export type CopilotResultPresentationSelection = ReturnType<typeof createResultPresentationSelection>
